import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'app/main/rootReducer';
import { makeStyles, Paper, Typography } from '@material-ui/core';
import { DonutGraph } from './DonutGraph/DonutGraph';
import { IBooking, IBookingsCountByService, IBookingsCountByServiceData, ISchedule } from 'app/app.types';
import { IVenueBookings, IVenueScheduleDetailsByTheDate, IVenueTablesLayoutsForTheDay } from 'app/reducers/venueSlice';
import { IBookingsCountByServiceWrapperProps } from './types';

const NS = 'BookingsCountByServiceWrapper';

const useStyles = makeStyles({
  tableTitle: {
    fontWeight: 'normal',
    margin: '8px 8px 8px 50px'
  },
  containerPaper: {
    marginTop: '10px',
    minHeight: '400px',
    minWidth: '400px'
  },
  graphDatePicker: {
    margin: '10px'
  },
  donutGraphs: {
    display: 'flex',
    margin: '0 15px 0 15px',
    flexWrap: 'wrap'
  },
  venueHeader: {
    display: 'flex',
    marginLeft: '25px'
  }
});

const initialBookingsCountByServiceState: IBookingsCountByServiceData = {
  totalBookings: 0,
  bookingsCountByServices: [] as IBookingsCountByService[]
};

/**
 * Displays the average spend booking size in bar graph with pagination.
 */
export const BookingsCountByServiceWrapper = ({venueId, venueName, randomGraphColor }: IBookingsCountByServiceWrapperProps) => {
  const classes = useStyles();
  const { bookingsByDateForServiceGroupingPaxGrouping,
    tablesLayoutsForTheDay,
    scheduleDetailsByTheDate } = useSelector((state: RootState) => state.venueReducer);
  const { filterParameters } = useSelector((state: RootState) => state.dashboardReducer);
  const [ bookingsByServices, setBookingsByServices ] = useState<IBookingsCountByServiceData>(initialBookingsCountByServiceState);
  const [ venueBasedBookingsPaxGroups, setVenueBasedBookingsPaxGroups] = useState<IVenueBookings>(
    { venueId: venueId, bookings: [] } as IVenueBookings);
  const [ venueBasedScheduleDetails, setVenueBasedScheduleDetails] = useState<IVenueScheduleDetailsByTheDate>(
    { venueId: venueId, scheduleDetailsByTheDate: { services: []} as ISchedule} as IVenueScheduleDetailsByTheDate);
  const [ venueBasedTablesLayouts, setVenueBasedTablesLayouts ] = useState<IVenueTablesLayoutsForTheDay>(
    {venueId: venueId, tablesLayoutsForTheDay: null } as IVenueTablesLayoutsForTheDay);

  useEffect(() => {
    var filteredVenueBasedBookingsPaxGroups = bookingsByDateForServiceGroupingPaxGrouping.map(item => ({
      venueId: item.venueId,
      bookings: item.bookings.filter(f => f.status.statusType.toLowerCase() !== "cancelled")
    } as IVenueBookings));

    setVenueBasedBookingsPaxGroups(filteredVenueBasedBookingsPaxGroups.find(f => f.venueId === venueId));
    setVenueBasedScheduleDetails(scheduleDetailsByTheDate.find(f => f.venueId === venueId));
    setVenueBasedTablesLayouts(tablesLayoutsForTheDay.find(f => f.venueId === venueId));
  }, [ bookingsByDateForServiceGroupingPaxGrouping, scheduleDetailsByTheDate ])

  useEffect(() => {

    if ((venueBasedBookingsPaxGroups?.bookings.length ?? 0) === 0
      || (venueBasedScheduleDetails?.scheduleDetailsByTheDate?.services.length ?? 0) === 0) {
      setBookingsByServices(initialBookingsCountByServiceState);
      return;
    }

    const counts = venueBasedBookingsPaxGroups.bookings.reduce((r: any, a: IBooking) => {
      const serviceId = a.serviceId || "Others";
      if (!r.hasOwnProperty(serviceId)) {
        r[serviceId] = 0;
      }
      r[serviceId]++;
      return r;
    }, []);

    if (Object.keys(counts).length > 0) {
      const countsByServiceTypes = Object.keys(counts).map(k => {
        return {
          serviceId: k,
          serviceName: scheduleDetailsByTheDate.flatMap(s => s.scheduleDetailsByTheDate || [])
            .flatMap(f => f.services || [])
            .find(f => f.id === k)?.name ?? "Others",
          totalBookings: counts[k]
        } as IBookingsCountByService;
      });

      setBookingsByServices({
        totalBookings: venueBasedBookingsPaxGroups.bookings.length,
        bookingsCountByServices: countsByServiceTypes
      });
    } else {
      setBookingsByServices(initialBookingsCountByServiceState)
    }
  }, [ venueBasedBookingsPaxGroups, venueBasedScheduleDetails ]);

  const getPercentageValue = (totalBookings:number, serviceBookings: number): number => {
    return Math.round((serviceBookings / totalBookings) * 100);
  }

  const getTotalPaxOfServiceTables = (serviceId: string): number => {
    // Get SectionIds from the Services by the service type
    const sectionsFromTheService =  scheduleDetailsByTheDate.flatMap(s => s.scheduleDetailsByTheDate || [])
      .flatMap(s => s.services || [])
      .filter(f => f.id === serviceId)
      .flatMap(f => f.sections)
      .filter(f => f != null)
      .map(f => f.id) ?? [];

    const totalServicePax = !venueBasedTablesLayouts
      ? 0
      : venueBasedTablesLayouts.tablesLayoutsForTheDay
          .filter(f => sectionsFromTheService.findIndex(fi => fi === f.sectionId) >= 0)
          .flatMap(layout => layout.tables.map(table => table.capacity))
          .reduce((accumulator, current) => accumulator + current, 0);

    return totalServicePax;
  }

  const getBookingsTotalForTheService = (serviceId: string): number => {
    return !venueBasedBookingsPaxGroups
      ? 0
      : venueBasedBookingsPaxGroups.bookings
        .filter(f => f.serviceId === serviceId)
        .reduce((accumulator, current) => accumulator + current.people, 0);
  }

  if (!filterParameters) return null;

  if (filterParameters.isLoading || filterParameters.isDirty) return null;

  return (<>
      <Typography variant="h5" gutterBottom component="div" className={classes.venueHeader}>{venueName}</Typography>
      { bookingsByServices.bookingsCountByServices.length > 0
            &&
      <>
        <div className={classes.donutGraphs}>
          { bookingsByServices.bookingsCountByServices.map(row => (
            <>
              <DonutGraph key={row.serviceId}
                legends={`Pax ${getBookingsTotalForTheService(row.serviceId)} out of ${getTotalPaxOfServiceTables(row.serviceId)}`}
                title={row.serviceName}
                valueLabel={`${getPercentageValue(bookingsByServices.totalBookings, row.totalBookings)}%`}
                donutGraphData={[
                  {angle: getPercentageValue(bookingsByServices.totalBookings, row.totalBookings), color: randomGraphColor},
                  {angle: getPercentageValue(bookingsByServices.totalBookings, bookingsByServices.totalBookings - row.totalBookings), color: '#DCDCDC'}]} />
            </>
          ))}
        </div>
        </>
        }
      { bookingsByServices.bookingsCountByServices.length === 0
        &&
        <Typography variant="h5" gutterBottom component="div">No data to present</Typography>
      }
  </>);
}
